import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { AbstractCalendarEvent } from '../helperClasses/CalendarEvent';

import { useConfiguredUrl } from '../../../../appRouter/url';
import { type Geschaeftsbereich, hiveExperteTerminStatus } from '../../../../dtos';
import { calendarColors } from '../../../../theme/colorPalette';
import { formatTimeRangeForDate } from '../../../../utils';
import { HoverCard, TooltipElement } from '../../HoverCard';
import { getSlashedLinesColor } from '../createCalendarColors.ts';
import { type CalendarEvent, isExperteEvent } from '../helperClasses';
import { EventButton } from './EventButton';

type ExternalSAPEventProps = {
  readonly calendarEvent: CalendarEvent;
};

const getGeschaeftsbereich = (calendarEvent: CalendarEvent): Geschaeftsbereich | null => {
  // only experteblockungen have a geschaeftsbereich, even when they do not point to a Veranstaltung in our system
  if (!isExperteEvent(calendarEvent)) {
    return null;
  }

  return calendarEvent.blockungData.geschaeftsbereich;
};

export const ExternalSAPEvent = ({ calendarEvent }: ExternalSAPEventProps) => {
  const { configuredUrl } = useConfiguredUrl();

  const gotoHive = (): void => {
    if (isExperteEvent(calendarEvent)) {
      const experteSapId = calendarEvent.ressourceSapId;
      const hiveUrl = configuredUrl.hive.experte.auslastung(experteSapId);

      window.open(hiveUrl, '_blank', 'noopener');
    }
  };

  const getEventColor = (event: AbstractCalendarEvent): string => {
    if (
      isExperteEvent(event) &&
      (event.blockungData.status === hiveExperteTerminStatus.GEPLANT ||
        (!event.blockungData.status && !event.blockungData.veranstaltung?.veranstaltungSapId))
    ) {
      return getSlashedLinesColor(alpha(calendarColors.externalBlockung, 0.5), calendarColors.externalBlockung);
    } else {
      return calendarColors.externalBlockung;
    }
  };

  return (
    <HoverCard
      title={
        <Stack gap={1} m={1}>
          <TooltipElement
            label="AKA-Veranstaltung-ID / Teilleistung-ID"
            value={calendarEvent.blockungData.quelleTerminId}
          />
          <TooltipElement label="Geschäftsbereich" value={getGeschaeftsbereich(calendarEvent) ?? '-'} />
          <TooltipElement label="Ort" value={calendarEvent.blockungData.locationKuerzel ?? '-'} />
          <TooltipElement
            label="Ablaufzeiten"
            value={calendarEvent.blockungData.zeitraeume
              .map(({ start, end }) => formatTimeRangeForDate(start, end))
              .join('\n')}
          />
        </Stack>
      }
    >
      <Box data-testid="sapEvent">
        <EventButton
          eventColor={getEventColor(calendarEvent)}
          sx={{ cursor: isExperteEvent(calendarEvent) ? 'pointer' : 'default' }}
          onClick={isExperteEvent(calendarEvent) ? (): void => gotoHive() : undefined}
        >
          <Stack>
            <Typography
              variant="paragraphSm"
              color="textPrimaryOnBrand"
              textAlign="center"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {calendarEvent.blockungData.quelleTerminId}
            </Typography>

            <Typography variant="paragraphSm" fontSize=".5rem">
              {getGeschaeftsbereich(calendarEvent) ?? (
                <div>
                  <br />
                </div>
              )}
            </Typography>
          </Stack>
        </EventButton>
      </Box>
    </HoverCard>
  );
};
